/**
 * DateRangeInput wraps DateRangePicker from React-dates and gives a list of all default props we use.
 * Styles for DateRangePicker can be found from 'public/reactDates.css'.
 *
 * N.B. *isOutsideRange* in defaultProps is defining what dates are available to booking.
 */
import React, { useState, useRef, useEffect } from 'react';
import { bool, func, instanceOf, oneOf, shape, string } from 'prop-types';
import { DateRangePicker } from 'react-dates';
import classNames from 'classnames';
import moment from 'moment';
import { START_DATE, END_DATE } from '../../util/dates';
import { IconArrowHead } from '../../components';
import css from './DateRangeInput.module.css';

export const HORIZONTAL_ORIENTATION = 'horizontal';
export const ANCHOR_LEFT = 'left';

// When the unit type is day, the endDate of booking range is exclusive.
// In the UI picker, we show only inclusive dates
const apiEndDateToPickerDate = (isDaily, endDate) => {
  const isValid = endDate instanceof Date;

  // API end dates are exlusive, so we need to shift them with daily
  // booking.
  return isValid && isDaily
    ? moment(endDate).subtract(1, 'days')
    : isValid
    ? moment(endDate)
    : null;
};

// When the unit type is day, the endDate of booking range is exclusive.
// In the UI picker, we show only inclusive dates
const pickerEndDateToApiDate = (isDaily, endDate) => {
  const isValid = endDate instanceof moment;

  // API end dates are exlusive, so we need to shift them with daily
  // booking.
  return isValid && isDaily
    ? endDate
        .clone()
        .add(1, 'days')
        .toDate()
    : isValid
    ? endDate.toDate()
    : null;
};

// Since final-form tracks the onBlur event for marking the field as
// touched (which triggers possible error validation rendering), only
// trigger the event asynchronously when no other input within this
// component has received focus.
//
// This prevents showing the validation error when the user selects a
// value and moves on to another input within this component.
const BLUR_TIMEOUT = 100;

// IconArrowHead component might not be defined if exposed directly to the file.
// This component is called before IconArrowHead component in components/index.js
const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

function DateRangeInputComponent(props) {
  const [focusedInput, setFocusedInput] = useState(null);
  const [currentStartDate, setCurrentStartDate] = useState(null);
  const blurTimeoutId = useRef(null);

  const {
      className = null,
      useMobileMargins = false,
      initialDates = null,
      value = null,
      _startDateId = 'startDate',
      _endDateId = 'endDate',
      startDatePlaceholderText = null,
      endDatePlaceholderText = null,
      _disabled = false,
      _required = false,
      _readOnly = false,
      screenReaderInputMessage = null,
      _showClearDates = false,
      _showDefaultInputIcon = false,
      _customArrowIcon = <span />,
      _customInputIcon = null,
      _customCloseIcon = null,
      _noBorder = true,
      _block = false,
      _renderMonthText = null,
      _orientation = HORIZONTAL_ORIENTATION,
      _anchorDirection = ANCHOR_LEFT,
      _horizontalMargin = 0,
      _withPortal = false,
      _withFullScreenPortal = false,
      _appendToBody = false,
      _disableScroll = false,
      _daySize = 38,
      _isRTL = false,
      _initialVisibleMonth = null,
      _firstDayOfWeek = 0,
      _numberOfMonths = 1,
      _keepOpenOnDateSelect = false,
      _reopenPickerOnClearDates = false,
      _renderCalendarInfo = null,
      _hideKeyboardShortcutsPanel = true,
      _navPrev = <PrevIcon />,
      _navNext = <NextIcon />,
      _onPrevMonthClick = () => {},
      _onNextMonthClick = () => {},
      _transitionDuration = 200,
      _renderCalendarDay = undefined,
      _renderDayContents = day => <span className="renderedDay">{day.format('D')}</span>,
      _minimumNights = 0,
      _enableOutsideDays = false,
      isDayBlocked = () => () => false,
      isOutsideRange = _day => false,
      _isDayHighlighted = () => {},
      _displayFormat = 'ddd, MMM D',
      _monthFormat = 'MMMM YYYY',
      _weekDayFormat = 'dd',
      
      // Props that were not in defaultProps but used in destructuring
      isDaily,
      name,
      onBlur,
      onChange,
      onFocus,
      isBlockedBetween,
      
      ...datePickerProps
  } = props;

  useEffect(() => {
    return () => {
      window.clearTimeout(blurTimeoutId.current);
    };
  }, []);

  useEffect(() => {
    if (props.focusedInput && props.focusedInput !== focusedInput) {
      setFocusedInput(props.focusedInput);
    }
  }, [props.focusedInput]);

  const handleDatesChange = dates => {
    const { startDate, endDate } = dates;
    const startDateUpdated =
      isBlockedBetween &&
      startDate &&
      endDate &&
      currentStartDate &&
      startDate.isBefore(currentStartDate);
    let startDateAsDate = startDate instanceof moment ? startDate.startOf('day').toDate() : null;
    let endDateAsDate = pickerEndDateToApiDate(isDaily, endDate);
    if (startDateUpdated) {
      const clearEndDate = isBlockedBetween(startDate, moment(currentStartDate).add(1, 'days'));
      endDateAsDate = clearEndDate ? null : pickerEndDateToApiDate(isDaily, endDate);
    }
    setCurrentStartDate(startDateAsDate);
    onChange({ startDate: startDateAsDate, endDate: endDateAsDate });
  };

  const handleFocusChange = input => {
    setFocusedInput(input);
    if (input) {
      window.clearTimeout(blurTimeoutId.current);
      onFocus(input);
    } else {
      window.clearTimeout(blurTimeoutId.current);
      blurTimeoutId.current = window.setTimeout(() => {
        onBlur();
      }, BLUR_TIMEOUT);
    }
  };

  const initialStartMoment = initialDates ? moment(initialDates.startDate) : null;
  const initialEndMoment = initialDates ? moment(initialDates.endDate) : null;
  const startDate =
    value && value.startDate instanceof Date ? moment(value.startDate) : initialStartMoment;
  const endDate = apiEndDateToPickerDate(isDaily, value ? value.endDate : null) || initialEndMoment;

  // Inline i18n: use English placeholder and screen reader text
  const startDatePlaceholderTxt = startDatePlaceholderText || 'Start date';
  const endDatePlaceholderTxt = endDatePlaceholderText || 'End date';
  const screenReaderInputText =
    screenReaderInputMessage ||
    'Choose a date range. Press the down arrow key to interact with the calendar and select dates. Press the escape key to close the calendar.';

  const classes = classNames(css.inputRoot, className, {
    [css.withMobileMargins]: useMobileMargins,
  });

  return (
    <div className={classes}>
      <DateRangePicker
        {...datePickerProps}
        isDayBlocked={isDayBlocked(focusedInput)}
        isOutsideRange={isOutsideRange(focusedInput)}
        focusedInput={focusedInput}
        onFocusChange={handleFocusChange}
        startDate={startDate}
        endDate={endDate}
        minimumNights={isDaily ? 0 : 1}
        onDatesChange={handleDatesChange}
        startDatePlaceholderText={startDatePlaceholderTxt}
        endDatePlaceholderText={endDatePlaceholderTxt}
        screenReaderInputMessage={screenReaderInputText}
      />
    </div>
  );
}

DateRangeInputComponent.propTypes = {
  className: string,
  startDateId: string,
  endDateId: string,
  isDaily: bool.isRequired,
  focusedInput: oneOf([START_DATE, END_DATE]),
  initialDates: instanceOf(Date),
  name: string.isRequired,
  isBlockedBetween: func,
  isDayBlocked: func,
  isOutsideRange: func,
  onChange: func.isRequired,
  onBlur: func.isRequired,
  onFocus: func.isRequired,
  useMobileMargins: bool,
  startDatePlaceholderText: string,
  endDatePlaceholderText: string,
  screenReaderInputMessage: string,
  value: shape({
    startDate: instanceOf(Date),
    endDate: instanceOf(Date),
  }),
};

export default DateRangeInputComponent;
